import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { extractErrorMessage } from '@/lib/errors';
import { cn } from '@/lib/utils';
import { useEffect, useRef, useState } from 'react';

interface InlineTextFieldProps {
  value: string | null;
  onSave: (next: string | null) => Promise<void> | void;
  type?: 'text' | 'email' | 'url' | 'tel';
  multiline?: boolean;
  placeholder?: string;
  emptyLabel?: string;
  disabled?: boolean;
  className?: string;
  renderDisplay?: (value: string | null) => React.ReactNode;
}

export function InlineTextField({
  value,
  onSave,
  type = 'text',
  multiline = false,
  placeholder = 'Click to edit',
  emptyLabel = '—',
  disabled = false,
  className,
  renderDisplay,
}: InlineTextFieldProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const commitGuard = useRef(false);

  useEffect(() => {
    if (!isEditing) setDraft(value ?? '');
  }, [value, isEditing]);

  const commit = async () => {
    if (commitGuard.current) return;
    commitGuard.current = true;
    setError(null);
    const trimmed = draft.trim();
    const next = trimmed.length > 0 ? trimmed : null;
    if (next === (value ?? null)) {
      setIsEditing(false);
      commitGuard.current = false;
      return;
    }
    setIsSaving(true);
    try {
      await onSave(next);
      setIsEditing(false);
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'Failed to save'));
    } finally {
      setIsSaving(false);
      commitGuard.current = false;
    }
  };

  const cancel = () => {
    setDraft(value ?? '');
    setError(null);
    setIsEditing(false);
  };

  if (!isEditing) {
    const hasValue = value !== null && value !== '';
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsEditing(true)}
        className={cn(
          // wrap-anywhere: a contact value must stay readable in full, and one long unbreakable
          // token would otherwise spill out of the card
          'w-full wrap-anywhere rounded px-2 py-1 text-left text-sm transition-colors',
          hasValue ? 'text-foreground' : 'text-muted-foreground',
          !disabled && 'hover:bg-muted/50 cursor-text',
          disabled && 'cursor-default',
          className
        )}
      >
        {renderDisplay
          ? renderDisplay(value)
          : hasValue
            ? value
            : emptyLabel}
      </button>
    );
  }

  if (multiline) {
    return (
      <div className={cn('space-y-1', className)}>
        <Textarea
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              cancel();
            }
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              void commit();
            }
          }}
          placeholder={placeholder}
          disabled={isSaving}
          className="min-h-[68px] text-sm"
        />
        {error ? <p className="text-xs text-destructive">{error}</p> : null}
      </div>
    );
  }

  return (
    <div className={cn('space-y-1', className)}>
      <Input
        autoFocus
        type={type}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            void commit();
          }
          if (e.key === 'Escape') {
            e.preventDefault();
            cancel();
          }
        }}
        placeholder={placeholder}
        disabled={isSaving}
        className="h-9 text-sm"
      />
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}

